import {
  DashboardOutlined as DashboardIcon,
  ModelTraining as ModelTrainingIcon,
  SettingsOutlined as SettingsIcon,
  BusinessOutlined as TenantIcon,
  UploadFileOutlined as UploadFileIcon,
} from '@mui/icons-material';
import { DatabaseSearch } from 'lucide-react';
import { CipIcon } from '@/components/icons/CipIcon';
import { NavigationGroup, NavigationItem } from '@/types/navigation';
import { ROLES } from '@/constants/roles';

export const navigationGroups: NavigationGroup[] = [
  {
    id: 'cip-strategic-planning',
    title: 'CIP STRATEGIC PLANNING',
    items: [
      {
        id: 'optimization-scenarios',
        label: 'Optimization Scenarios',
        icon: CipIcon,
        path: '/optimization-scenarios',
        requiredRoles: [ROLES.VIEWER, ROLES.DATA_ADMIN],
      },
    ],
  },
  {
    id: 'portfolio-manager-group',
    title: '',
    items: [
      {
        id: 'portfolio-manager',
        label: 'Portfolio Manager',
        icon: DashboardIcon,
        path: '/portfolio-manager',
        requiredRoles: [ROLES.VIEWER, ROLES.DATA_ADMIN],
      },
    ],
  },{
    id: 'infrastructure-services',
    title: 'INFRASTRUCTURE SERVICES',
    items: [
      {
        id: 'water-network',
        label: 'Water Network',
        icon: DashboardIcon,
        path: '/dashboard',
        isExpandable: true,
        children: [
          {
            id: 'water-asset-inventory',
            label: 'Asset Inventory',
            path: '/water-network/asset-inventory',
          },
          {
            id: 'risk-modeling',
            label: 'Risk Modeling',
            path: '/water-network/risk-modeling/predictions/dashboard',
            isExpandable: true,
            requiredRoles: [ROLES.VIEWER, ROLES.DATA_ADMIN],
            children: [
              {
                id: 'predictions',
                label: 'Predict',
                path: '/water-network/risk-modeling/predictions',
                requiredRoles: [ROLES.VIEWER, ROLES.DATA_ADMIN],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'data-operations',
    title: 'DATA & OPERATIONS',
    items: [
      {
        id: 'data-hub',
        label: 'Data Hub',
        icon: DatabaseSearch,
        isExpandable: true,
        children: [
          {
            id: 'data-mapping',
            label: 'Data Mapping',
            path: '/data-hub/data-mapping',
          },
        ],
      },
    ],
  },
  {
    id: 'model-management',
    title: 'MODEL MANAGEMENT',
    items: [
      {
        id: 'model-studio',
        label: 'Model Studio',
        icon: ModelTrainingIcon,
        isExpandable: true,
        children: [
          {
            id: 'scenarios',
            label: 'Scenarios',
            path: '/model-studio/scenarios',
          },
          {
            id: 'failure-analysis',
            label: 'Failure Analysis',
            isExpandable: true,
            isSpecial: true,
            children: [
              {
                id: 'break-predict',
                label: 'Break Predict',
                path: '/model-studio/failure-analysis/break-predict',
                requiredRoles: [ROLES.VIEWER, ROLES.DATA_ADMIN],
              },
            ],
          },
          {
            id: 'survival-analysis',
            label: 'Survival Analysis',
            isExpandable: true,
            isSpecial: true,
            children: [
              {
                id: 'cohort-models',
                label: 'Cohort Models',
                path: '/model-studio/survival-analysis/cohort-models',
                requiredRoles: [ROLES.VIEWER, ROLES.DATA_ADMIN],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'data-conduit',
    title: 'DATA CONDUIT',
    items: [
      {
        id: 'job-setup',
        label: 'Job Setup',
        icon: UploadFileIcon,
        path: '/data-conduit/job-setup',
        requiredRoles: [ROLES.DATA_MANAGER],
      },
    ],
  },
];

export const footerNavigationItems: NavigationItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon,
    isExpandable: true,
  },
  {
    id: 'demonstration-tenant',
    label: 'Demonstration Tenant',
    icon: TenantIcon,
    isExpandable: true,
  },
];

const flattenNavigationItems = (items: NavigationItem[]): NavigationItem[] => {
  return items.flatMap((item) => [
    item,
    ...(item.children ? flattenNavigationItems(item.children) : []),
  ]);
};

const _allItems = navigationGroups.flatMap((group) => flattenNavigationItems(group.items));

export const getAllNavigationItems = () => _allItems;

export const findNavigationItemByPath = (path: string) =>
  _allItems.find((item) => item.path === path);

export function getSiblingItems(
  path: string,
  groups: NavigationGroup[] = navigationGroups,
): NavigationItem[] {
  function search(items: NavigationItem[]): NavigationItem[] | null {
    for (const item of items) {
      if (!item.children) continue;
      if (item.children.some((child) => child.path === path)) {
        return item.children.filter((child) => Boolean(child.path));
      }
      const found = search(item.children);
      if (found !== null) return found;
    }
    return null;
  }

  for (const group of groups) {
    if (group.items.some((item) => item.path === path)) {
      return group.items.filter((item) => Boolean(item.path));
    }
    const result = search(group.items);
    if (result !== null) return result;
  }
  return [];
}
